import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired, getContainerStyles } from '../../utils/formUtils';

interface EducationTrainingProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // High School
  high_school_name: string;
  high_school_city: string;
  high_school_state: string;
  high_school_country: string;
  is_high_school_graduate: string;
  graduation_year: string;
  highest_grade: string;

  // GED
  has_ged: string;
  ged_completion_year: string;

  // High School Courses
  courses_general: boolean;
  courses_academic: boolean;
  courses_vocational: boolean;
  courses_college_prep: boolean;

  // Special Education
  has_special_education: string;
  special_education_subjects: string;
  special_education_accommodations: string;

  // Technical/Vocational Training
  has_technical_training: string;
  technical_training_type: string;
  technical_school_name: string;
  technical_school_city: string;
  technical_school_state: string;
  technical_school_country: string;
  technical_years_attended_from: string;
  technical_years_attended_to: string;
  technical_graduated: string;
  technical_field_of_study: string;

  // State Vocational Rehabilitation
  has_vocational_rehabilitation: string;
  vocational_rehabilitation_year: string;
  vocational_rehabilitation_counselor: string;

  // Military Service
  has_military_service: string;
  military_year: string;
  military_branch: string;
  military_mos: string;

  // College 1
  college1_name: string;
  college1_year_attended: string;
  college1_degree_received: string;
  college1_major: string;

  // College 2
  college2_name: string;
  college2_year_attended: string;
  college2_degree_received: string;
  college2_major: string;

  // College 3
  college3_name: string;
  college3_year_attended: string;
  college3_degree_received: string;
  college3_major: string;
}

const EducationTraining: React.FC<EducationTrainingProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  // Use new props if available, otherwise fall back to old props
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => {
      /* noop */
    });

  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'education_training', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'education_training', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // High School
    high_school_name: '',
    high_school_city: '',
    high_school_state: '',
    high_school_country: '',
    is_high_school_graduate: '',
    graduation_year: '',
    highest_grade: '',

    // GED
    has_ged: '',
    ged_completion_year: '',

    // High School Courses
    courses_general: false,
    courses_academic: false,
    courses_vocational: false,
    courses_college_prep: false,

    // Special Education
    has_special_education: '',
    special_education_subjects: '',
    special_education_accommodations: '',

    // Technical/Vocational Training
    has_technical_training: '',
    technical_training_type: '',
    technical_school_name: '',
    technical_school_city: '',
    technical_school_state: '',
    technical_school_country: '',
    technical_years_attended_from: '',
    technical_years_attended_to: '',
    technical_graduated: '',
    technical_field_of_study: '',

    // State Vocational Rehabilitation
    has_vocational_rehabilitation: '',
    vocational_rehabilitation_year: '',
    vocational_rehabilitation_counselor: '',

    // Military Service
    has_military_service: '',
    military_year: '',
    military_branch: '',
    military_mos: '',

    // College 1
    college1_name: '',
    college1_year_attended: '',
    college1_degree_received: '',
    college1_major: '',

    // College 2
    college2_name: '',
    college2_year_attended: '',
    college2_degree_received: '',
    college2_major: '',

    // College 3
    college3_name: '',
    college3_year_attended: '',
    college3_degree_received: '',
    college3_major: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Education, Training & Military Information
        {readOnly && (
          <Typography
            component='span'
            variant='body2'
            color='warning.main'
            sx={{ ml: 2, fontStyle: 'italic', fontWeight: 'bold' }}
          >
            (Read Only - View Only)
          </Typography>
        )}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        ...getContainerStyles(readOnly)
      }}>
        {/* High School Information */}
        {!isFieldHiddenLocal('high_school_name') && (
          <Box>
            <Typography variant='h6' gutterBottom>
              High School Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='High School Name'
                value={formData.high_school_name}
                onChange={e => handleChange('high_school_name', e.target.value)}
                required={isFieldRequiredLocal('high_school_name')}
                sx={{ flex: 1, minWidth: 300 }}
                disabled={readOnly}
              />
            <TextField
              label='City'
              value={formData.high_school_city}
              onChange={e => handleChange('high_school_city', e.target.value)}
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label='State'
              value={formData.high_school_state}
              onChange={e => handleChange('high_school_state', e.target.value)}
              sx={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label='Country'
              value={formData.high_school_country}
              onChange={e =>
                handleChange('high_school_country', e.target.value)
              }
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle1' gutterBottom>
              High School Graduate
            </Typography>
            <FormControl component='fieldset'>
              <RadioGroup
                value={formData.is_high_school_graduate}
                onChange={e =>
                  handleChange('is_high_school_graduate', e.target.value)
                }
              >
                <FormControlLabel value='yes' control={<Radio />} label='Yes' />
                <FormControlLabel value='no' control={<Radio />} label='No' />
              </RadioGroup>
            </FormControl>
          </Box>

          {formData.is_high_school_graduate === 'yes' ? (
            <TextField
              fullWidth
              label='Year of Graduation'
              value={formData.graduation_year}
              onChange={e => handleChange('graduation_year', e.target.value)}
              sx={{ mt: 2 }}
            />
          ) : (
            <TextField
              fullWidth
              label='Highest grade completed'
              value={formData.highest_grade}
              onChange={e => handleChange('highest_grade', e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </Box>
        )}

        {/* GED */}
        <Box>
          <Typography variant='h6' gutterBottom>
            High School Equivalency Diploma/GED
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_ged}
              onChange={e => handleChange('has_ged', e.target.value)}
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_ged === 'yes' && (
            <TextField
              fullWidth
              label='Year of Completion'
              value={formData.ged_completion_year}
              onChange={e =>
                handleChange('ged_completion_year', e.target.value)
              }
            />
          )}
        </Box>

        {/* High School Courses */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Courses or Training in High School
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.courses_general}
                  onChange={e =>
                    handleChange('courses_general', e.target.checked)
                  }
                />
              }
              label='General'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.courses_academic}
                  onChange={e =>
                    handleChange('courses_academic', e.target.checked)
                  }
                />
              }
              label='Academic'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.courses_vocational}
                  onChange={e =>
                    handleChange('courses_vocational', e.target.checked)
                  }
                />
              }
              label='Vocational'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.courses_college_prep}
                  onChange={e =>
                    handleChange('courses_college_prep', e.target.checked)
                  }
                />
              }
              label='College Prep'
            />
          </Box>
        </Box>

        {/* Special Education */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Special Education
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_special_education}
              onChange={e =>
                handleChange('has_special_education', e.target.value)
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_special_education === 'yes' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label='Subjects'
                value={formData.special_education_subjects}
                onChange={e =>
                  handleChange('special_education_subjects', e.target.value)
                }
              />
              <TextField
                fullWidth
                label='Accommodations'
                multiline
                rows={3}
                value={formData.special_education_accommodations}
                onChange={e =>
                  handleChange(
                    'special_education_accommodations',
                    e.target.value
                  )
                }
              />
            </Box>
          )}
        </Box>

        {/* Technical/Vocational Training */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Technical or Vocational Training
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_technical_training}
              onChange={e =>
                handleChange('has_technical_training', e.target.value)
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_technical_training === 'yes' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label='Type of Training'
                value={formData.technical_training_type}
                onChange={e =>
                  handleChange('technical_training_type', e.target.value)
                }
              />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label='Name of School'
                  value={formData.technical_school_name}
                  onChange={e =>
                    handleChange('technical_school_name', e.target.value)
                  }
                  sx={{ flex: 1, minWidth: 300 }}
                />
                <TextField
                  label='City'
                  value={formData.technical_school_city}
                  onChange={e =>
                    handleChange('technical_school_city', e.target.value)
                  }
                  sx={{ flex: 1, minWidth: 150 }}
                />
                <TextField
                  label='State'
                  value={formData.technical_school_state}
                  onChange={e =>
                    handleChange('technical_school_state', e.target.value)
                  }
                  sx={{ flex: 1, minWidth: 150 }}
                />
                <TextField
                  label='Country'
                  value={formData.technical_school_country}
                  onChange={e =>
                    handleChange('technical_school_country', e.target.value)
                  }
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label='Years Attended From'
                  type='date'
                  value={formData.technical_years_attended_from}
                  onChange={e =>
                    handleChange(
                      'technical_years_attended_from',
                      e.target.value
                    )
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label='Years Attended To'
                  type='date'
                  value={formData.technical_years_attended_to}
                  onChange={e =>
                    handleChange('technical_years_attended_to', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>
              <FormControl component='fieldset'>
                <Typography variant='subtitle1' gutterBottom>
                  Did you Graduate
                </Typography>
                <RadioGroup
                  value={formData.technical_graduated}
                  onChange={e =>
                    handleChange('technical_graduated', e.target.value)
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                  />
                  <FormControlLabel value='no' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
              <TextField
                fullWidth
                label='Field of Study'
                value={formData.technical_field_of_study}
                onChange={e =>
                  handleChange('technical_field_of_study', e.target.value)
                }
              />
            </Box>
          )}
        </Box>

        {/* State Vocational Rehabilitation */}
        <Box>
          <Typography variant='h6' gutterBottom>
            State Vocational Rehabilitation
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_vocational_rehabilitation}
              onChange={e =>
                handleChange('has_vocational_rehabilitation', e.target.value)
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_vocational_rehabilitation === 'yes' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='Year Attended'
                value={formData.vocational_rehabilitation_year}
                onChange={e =>
                  handleChange('vocational_rehabilitation_year', e.target.value)
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label="Counselor's Name"
                value={formData.vocational_rehabilitation_counselor}
                onChange={e =>
                  handleChange(
                    'vocational_rehabilitation_counselor',
                    e.target.value
                  )
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>
          )}
        </Box>

        {/* Military Service */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Military Service
          </Typography>
          <FormControl component='fieldset' sx={{ mb: 2 }}>
            <RadioGroup
              value={formData.has_military_service}
              onChange={e =>
                handleChange('has_military_service', e.target.value)
              }
            >
              <FormControlLabel value='yes' control={<Radio />} label='Yes' />
              <FormControlLabel value='no' control={<Radio />} label='No' />
            </RadioGroup>
          </FormControl>
          {formData.has_military_service === 'yes' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='Year Attended'
                value={formData.military_year}
                onChange={e => handleChange('military_year', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label='Branch'
                value={formData.military_branch}
                onChange={e => handleChange('military_branch', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                label='MOS'
                value={formData.military_mos}
                onChange={e => handleChange('military_mos', e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
            </Box>
          )}
        </Box>

        {/* College 1 */}
        <Box>
          <Typography variant='h6' gutterBottom>
            (1) Name of the College
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='College Name'
              value={formData.college1_name}
              onChange={e => handleChange('college1_name', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='Year Attended'
                value={formData.college1_year_attended}
                onChange={e =>
                  handleChange('college1_year_attended', e.target.value)
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl component='fieldset' sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant='subtitle1' gutterBottom>
                  Degree Received
                </Typography>
                <RadioGroup
                  value={formData.college1_degree_received}
                  onChange={e =>
                    handleChange('college1_degree_received', e.target.value)
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                  />
                  <FormControlLabel value='no' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label='Major'
              value={formData.college1_major}
              onChange={e => handleChange('college1_major', e.target.value)}
            />
          </Box>
        </Box>

        {/* College 2 */}
        <Box>
          <Typography variant='h6' gutterBottom>
            (2) Name of the College
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='College Name'
              value={formData.college2_name}
              onChange={e => handleChange('college2_name', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='Year Attended'
                value={formData.college2_year_attended}
                onChange={e =>
                  handleChange('college2_year_attended', e.target.value)
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl component='fieldset' sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant='subtitle1' gutterBottom>
                  Degree Received
                </Typography>
                <RadioGroup
                  value={formData.college2_degree_received}
                  onChange={e =>
                    handleChange('college2_degree_received', e.target.value)
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                  />
                  <FormControlLabel value='no' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label='Major'
              value={formData.college2_major}
              onChange={e => handleChange('college2_major', e.target.value)}
            />
          </Box>
        </Box>

        {/* College 3 */}
        <Box>
          <Typography variant='h6' gutterBottom>
            (3) Name of the College
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label='College Name'
              value={formData.college3_name}
              onChange={e => handleChange('college3_name', e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label='Year Attended'
                value={formData.college3_year_attended}
                onChange={e =>
                  handleChange('college3_year_attended', e.target.value)
                }
                sx={{ flex: 1, minWidth: 200 }}
              />
              <FormControl component='fieldset' sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant='subtitle1' gutterBottom>
                  Degree Received
                </Typography>
                <RadioGroup
                  value={formData.college3_degree_received}
                  onChange={e =>
                    handleChange('college3_degree_received', e.target.value)
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Yes'
                  />
                  <FormControlLabel value='no' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label='Major'
              value={formData.college3_major}
              onChange={e => handleChange('college3_major', e.target.value)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EducationTraining;
